
import { useGameState } from "@/hooks/useGameState";
import { useBotWordGeneration } from "@/hooks/useBotWordGeneration";
import { useGameInitialization } from "@/hooks/useGameInitialization";
import { useWordSubmission } from "@/hooks/useWordSubmission";
import { useRoundProcessing } from "@/hooks/useRoundProcessing";
import { useAutoFocus } from "@/hooks/useAutoFocus";
import { useGameStateMonitoring } from "@/hooks/useGameStateMonitoring";

export const useGameHooks = (gameId: string | null, userId: string | null) => {
  // Use the game state hook
  const gameState = useGameState();
  
  const {
    rounds, setRounds,
    currentWord, setCurrentWord,
    isRevealed, setIsRevealed,
    usedWords, setUsedWords,
    gameOver, setGameOver,
    convergenceRound, setConvergenceRound,
    loading, setLoading,
    isSubmitting, setIsSubmitting,
    isOwner, setIsOwner,
    botHasChosenWord, setBotHasChosenWord,
    botIsThinking, setBotIsThinking,
    shouldAutoFocus, setShouldAutoFocus,
    gameStatus, setGameStatus,
    userHasSubmitted, setUserHasSubmitted,
    wordError, setWordError,
    processingComplete, setProcessingComplete
  } = gameState;

  // Use the auto-focus hook
  useAutoFocus(botIsThinking, setShouldAutoFocus);
  
  // Use the game state monitoring hook
  useGameStateMonitoring(
    gameId,
    rounds,
    botIsThinking, 
    botHasChosenWord,
    userHasSubmitted,
    processingComplete,
    isSubmitting
  );

  // Set up the round processing hook
  const { handleProcessBothWords } = useRoundProcessing(
    gameId,
    rounds,
    currentWord,
    usedWords,
    setRounds,
    setUsedWords,
    setCurrentWord,
    setIsRevealed,
    setBotHasChosenWord,
    setBotIsThinking,
    setShouldAutoFocus,
    setGameOver,
    setConvergenceRound,
    setGameStatus,
    setUserHasSubmitted,
    setIsSubmitting,
    setProcessingComplete
  );

  // Set up bot word generation
  const { generateBotWordForCurrentRound } = useBotWordGeneration(
    gameId,
    setBotIsThinking,
    setBotHasChosenWord,
    usedWords,
    userHasSubmitted,
    setUserHasSubmitted,
    currentWord,
    rounds,
    handleProcessBothWords
  );

  // Set up word submission
  const { handleSubmitWord, handleClearWordError } = useWordSubmission(
    gameId,
    rounds,
    currentWord,
    usedWords,
    setRounds,
    setUsedWords,
    setCurrentWord,
    setIsRevealed,
    setBotHasChosenWord,
    setBotIsThinking,
    setShouldAutoFocus,
    setGameOver,
    setConvergenceRound,
    setGameStatus,
    isSubmitting,
    setIsSubmitting,
    gameOver,
    botHasChosenWord,
    userHasSubmitted,
    setUserHasSubmitted,
    wordError,
    setWordError,
    processingComplete,
    setProcessingComplete,
    botIsThinking,
    handleProcessBothWords
  );

  // Set up game initialization
  useGameInitialization(
    gameId,
    userId,
    setLoading,
    setGameStatus,
    setGameOver,
    setConvergenceRound,
    setRounds,
    setUsedWords,
    setIsOwner,
    setBotHasChosenWord,
    generateBotWordForCurrentRound
  );

  return {
    // State from gameState
    ...gameState,
    // Functions from other hooks
    handleSubmitWord,
    handleClearWordError,
    generateBotWordForCurrentRound
  };
};
